import { Card, Container, Input } from "reactstrap";
import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Btn } from "../../../AbstractElements";
import { getTeachers, getTeacherLeaderTeachers } from "../../../Api/apiUtils";
import { isTeacherLeader, isTeacherLeaderDisabled, PROFILE_DATA_UPDATED_EVENT, getTeacherLeaderUserId } from "../../../utils/permissions";
import ClickToCall from "../../../Telephony/ClickToCall";

interface Teacher {
  teacherId: any;
  teacherName: string;
  emailId: string;
  mobileNumber: string;
  profilePhoto?: string;
  credentialId?: string;
  countryCode: any;
  isAcademicExpert?: boolean | string;
  enableForPaid?: boolean | string;
}

type TeacherTypeFilter = "all" | "paid" | "ae";

interface SearchPayload {
  name?: string;
  emailId?: string;
  mobileNumber?: string;
  type?: string;
  resigned?: boolean;
}

const mapTeacherLeaderRow = (row: any): Teacher => ({
  teacherId: row.teacherId,
  teacherName: row.teacherName ?? "",
  emailId: row.emailId ?? "",
  mobileNumber: row.mobileNumber ?? "",
  profilePhoto: row.profilePhoto,
  // List API does not return credentialId; teacherUserId is used for profile route.
  credentialId: row.credentialId != null ? String(row.credentialId) : String(row.teacherUserId ?? ""),
  countryCode: row.countryCode ?? "",
});

const SamplePageContainer: React.FC = () => {
  const [teachers, setTeachers] = useState<Teacher[]>([]);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(true);
  const [noResults, setNoResults] = useState<boolean>(false);
  const [includeTestTeachers, setIncludeTestTeachers] = useState<boolean>(false);
  const [includeResignedTeachers, setIncludeResignedTeachers] = useState<boolean>(false);
  const [teacherTypeFilter, setTeacherTypeFilter] = useState<TeacherTypeFilter>("all");
  const navigate = useNavigate();
  const tlDisabled = isTeacherLeaderDisabled();
  const isTeacherLeaderUser = isTeacherLeader();

  const debounce = (func: Function, delay: number) => {
    let timer: NodeJS.Timeout;
    return (...args: any[]) => {
      clearTimeout(timer);
      timer = setTimeout(() => func.apply(this, args), delay);
    };
  };

  const storeTeacherCredentialIds = (list: Teacher[]) => {
    const credentialIds = list.map((teacher) => teacher.credentialId).filter(Boolean);
    localStorage.setItem("TeachercredentialIds", JSON.stringify(credentialIds));
  };

  const fetchTeachers = async (searchPayload: SearchPayload = {}) => {
    setLoading(true);
    try {
      if (isTeacherLeaderUser) {
        const userId = getTeacherLeaderUserId();
        if (!userId) {
          setTeachers([]);
          setNoResults(true);
          return;
        }

        const fetchedTeachers = await getTeacherLeaderTeachers(userId);
        const mapped = fetchedTeachers.map(mapTeacherLeaderRow);
        setTeachers(mapped);
        setNoResults(mapped.length === 0);
        storeTeacherCredentialIds(mapped);
        return;
      }

      const payload: SearchPayload = {
        ...searchPayload,
        ...(includeTestTeachers && { type: "test" }),
        ...(includeResignedTeachers && { resigned: true }),
      };

      const fetchedTeachers = await getTeachers(0, 500, payload);
      if (Array.isArray(fetchedTeachers)) {
        setTeachers(fetchedTeachers);
        setNoResults(fetchedTeachers.length === 0);
        storeTeacherCredentialIds(fetchedTeachers);
      } else {
        console.error("Fetched data is not an array:", fetchedTeachers);
        setNoResults(true);
      }
    } catch (error) {
      console.error("There was an error fetching the teacher data!", error);
      setNoResults(true);
    } finally {
      setLoading(false);
    }
  };

  const debouncedSearch = useCallback(
    debounce(async (query: string) => {
      if (isTeacherLeaderUser) return;

      if (query.trim() === "") {
        await fetchTeachers();
        return;
      }

      let searchPayload: SearchPayload = {};
      if (new RegExp(/[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,15}/g).test(query)) {
        searchPayload = { emailId: query };
      } else if (new RegExp(/^[0-9\b]+$/).test(query)) {
        searchPayload = { mobileNumber: query };
      } else {
        searchPayload = { name: query };
      }

      await fetchTeachers(searchPayload);
    }, 500),
    [includeTestTeachers, includeResignedTeachers, isTeacherLeaderUser]
  );

  useEffect(() => {
    fetchTeachers();
  }, [includeTestTeachers, includeResignedTeachers, isTeacherLeaderUser]);

  useEffect(() => {
    if (!isTeacherLeaderUser) return;
    const refetchForTeacherLeader = () => fetchTeachers();
    window.addEventListener(PROFILE_DATA_UPDATED_EVENT, refetchForTeacherLeader);
    return () => window.removeEventListener(PROFILE_DATA_UPDATED_EVENT, refetchForTeacherLeader);
  }, [isTeacherLeaderUser, includeTestTeachers, includeResignedTeachers]);

  const visibleTeachers = teachers.filter((teacher) => {
    // API sends these flags as strings ("true"/"false")
    if (teacherTypeFilter === "ae") return String(teacher.isAcademicExpert) === "true";
    if (teacherTypeFilter === "paid") return String(teacher.enableForPaid) === "true";
    return true;
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (isTeacherLeaderUser) return;
    const value = e.target.value.trimStart();
    setSearchQuery(value);
    debouncedSearch(value);
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (isTeacherLeaderUser) return;
    if (e.key === "Enter") {
      debouncedSearch(searchQuery);
    }
  };

  const handleCardClick = (teacher: Teacher) => {
    if (teacher.credentialId && teacher.teacherId) {
      navigate(`/pages/teacher-profile/${teacher.credentialId}/${teacher.teacherId}`);
    } else {
      console.error("Missing credentialId or teacherId");
    }
  };

  function isValidImageUrl(url: string): boolean {
    if (!url) return false;
    try {
      new URL(url);
      return /\.(jpg|jpeg|png|gif|bmp|webp)$/i.test(url);
    } catch {
      return false;
    }
  }

  function getDisplayContent(teacher: any): React.ReactNode {
    if (teacher.teacherName && teacher.teacherName.trim().length > 0) {
      return teacher.teacherName.trim().charAt(0).toUpperCase();
    }

    if (teacher.profilePhoto && typeof teacher.profilePhoto === "string") {
      return teacher.profilePhoto.trim().charAt(0).toUpperCase();
    }

    return <i className="fas fa-user" style={{ fontSize: "24px" }}></i>;
  }

  return (
    <Container fluid>
      <Input
        type="text"
        placeholder="Search by teacher name, email, or mobile number..."
        value={searchQuery}
        onChange={handleInputChange}
        onKeyPress={handleKeyPress}
        disabled={isTeacherLeaderUser}
        title={isTeacherLeaderUser ? "Search disabled for Teacher Leaders" : undefined}
        className="mb-4"
      />

      <div style={{ display: "flex", alignItems: "center", gap: "50px" }}>
        <Btn
          className="mb-4"
          color="primary"
          disabled={tlDisabled}
          title={tlDisabled ? "Disabled for Teacher Leaders" : undefined}
          onClick={() => {
            if (tlDisabled) return;
            navigate("/pages/teacher-form");
          }}
        >
          Add Teacher
        </Btn>

        <div className="d-flex align-items-center mb-4" style={{ gap: "10px" }}>
          <label htmlFor="teacherTypeFilter" className="mb-0">
            <b>Teacher Type</b>
          </label>
          <Input
            id="teacherTypeFilter"
            type="select"
            title={isTeacherLeaderUser ? "Disabled for Teacher Leaders" : undefined}
            disabled={isTeacherLeaderUser}
            style={{ width: "160px" }}
            value={teacherTypeFilter}
            onChange={(e) => setTeacherTypeFilter(e.target.value as TeacherTypeFilter)}
          >
            <option value="all">All</option>
            <option value="paid">Paid</option>
            <option value="ae">AE's</option>
          </Input>
        </div>

        <div className="form-check form-switch">
          <input
            className="form-check-input"
            type="checkbox"
            role="switch"
            style={{ transform: "scale(1.4)" }}
            id="testTeacherToggle"
            checked={includeTestTeachers}
            disabled={isTeacherLeaderUser}
            title={isTeacherLeaderUser ? "Disabled for Teacher Leaders" : undefined}
            onChange={() => setIncludeTestTeachers(!includeTestTeachers)}
          />
          <label
            className="form-check-label"
            htmlFor="testTeacherToggle"
            style={{ marginLeft: "10px", fontSize: "15px", opacity: isTeacherLeaderUser ? 0.55 : 1 }}
          >
            <b>Include Test Teacher</b>
          </label>
        </div>

        <div className="form-check form-switch">
          <input
            className="form-check-input"
            type="checkbox"
            role="switch"
            style={{ transform: "scale(1.4)" }}
            id="resignedTeacherToggle"
            checked={includeResignedTeachers}
            disabled={isTeacherLeaderUser}
            title={isTeacherLeaderUser ? "Disabled for Teacher Leaders" : undefined}
            onChange={() => setIncludeResignedTeachers(!includeResignedTeachers)}
          />
          <label
            className="form-check-label"
            htmlFor="resignedTeacherToggle"
            style={{ marginLeft: "10px", fontSize: "15px", opacity: isTeacherLeaderUser ? 0.55 : 1 }}
          >
            <b>Include Resigned Teacher</b>
          </label>
        </div>
      </div>

      {loading ? (
        <div className="text-center">
          <p>Loading teachers...</p>
        </div>
      ) : noResults || visibleTeachers.length === 0 ? (
        <div className="text-center">
          <p>No teachers found matching your search criteria.</p>
        </div>
      ) : (
        <Card style={{ maxHeight: "70vh", overflowY: "auto" }}>
          <div className="email-right-aside">
            <div className="email-body email-list show card">
              <div className="mail-body-wrapper">
                <ul className="simple-list list-group">
                  {visibleTeachers.map((teacher) => (
                    <li
                      className="inbox-data list-group-item d-flex align-items-center"
                      key={teacher.teacherId}
                      onClick={() => handleCardClick(teacher)}
                    >
                      <div className="d-flex align-items-center">
                        <div className="social-img-wrap" style={{ width: "60px", height: "60px" }}>
                          <div className="social-img" style={{ width: "100%", height: "100%" }}>
                            {teacher.profilePhoto && isValidImageUrl(teacher.profilePhoto) ? (
                              <img
                                src={teacher.profilePhoto}
                                className="img-fluid"
                                alt="user"
                                style={{
                                  width: "100%",
                                  height: "100%",
                                  borderRadius: "50%",
                                  objectFit: "cover",
                                }}
                                onError={(e: React.SyntheticEvent<HTMLImageElement, Event>) => {
                                  const target = e.target as HTMLImageElement;
                                  target.style.display = "none";
                                }}
                              />
                            ) : (
                              <div
                                style={{
                                  width: "100%",
                                  height: "100%",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  backgroundColor: "#ccc",
                                  borderRadius: "50%",
                                  color: "#fff",
                                  fontSize: "24px",
                                  fontWeight: "bold",
                                }}
                              >
                                {getDisplayContent(teacher)}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      <div
                        style={{
                          flex: 1,
                          display: "flex",
                          alignItems: "center",
                          gap: "100px",
                          marginLeft: "30px",
                        }}
                      >
                        <div style={{ minWidth: "300px" }}>
                          <b>{teacher.teacherName}</b>
                        </div>
                        <div style={{ minWidth: "300px" }}>{teacher.emailId || "-"}</div>
                        <div style={{ minWidth: "150px" }}>
                          <ClickToCall
                            number={
                              teacher.mobileNumber
                                ? `${teacher.countryCode || ""}${teacher.mobileNumber}`
                                : undefined
                            }
                            name={teacher.teacherName}
                            sourceRecordId={teacher.teacherId}
                          />
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </Card>
      )}
    </Container>
  );
};

export default SamplePageContainer;
